import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
	Layout,
	Input,
	Button,
	Divider,
	Skeleton,
	message,
	Modal,
	Checkbox,
	Empty,
} from 'antd';
import {
	SearchOutlined,
	FilterOutlined,
	CloseOutlined,
	PlusOutlined,
	HomeOutlined,
	FolderOutlined,
	RightOutlined,
	DeleteOutlined,
	FileImageOutlined,
	FileOutlined,
	VideoCameraOutlined,
	FilePdfOutlined,
	FileWordOutlined,
	FileExcelOutlined,
	FilePptOutlined,
	AudioOutlined,
	EditOutlined,
	ArrowUpOutlined,
	UploadOutlined,
	FolderAddOutlined,
} from '@ant-design/icons';
import DashboardLayout from '../components/DashboardLayout';
import FolderCreateModal from '../components/media/FolderCreateModal';
import FileUploadModal from '../components/media/FileUploadModal';
import MediaPreviewModal from '../components/media/MediaPreviewModal';
import FolderEditModal from '../components/media/FolderEditModal';
import { mediaApi } from '../api/media';
import { tagsApi } from '../api/tags';
import { useAuth } from '../context/AuthContext';
import './DashboardFiles.css';
import { s3Api } from '../api/s3';
import { debounce } from 'lodash';
import '../styles/CommonModal.css';

const { Content, Sider } = Layout;
const { Search } = Input;

function DashboardFiles() {
	const { orgData } = useAuth();
	const [showFilters, setShowFilters] = useState(false);
	const [isFolderCreateModalVisible, setIsFolderCreateModalVisible] =
		useState(false);
	const [folders, setFolders] = useState([]);
	const [files, setFiles] = useState([]);
	const [tags, setTags] = useState([]);
	const [loading, setLoading] = useState(true);
	const [currentFolderId, setCurrentFolderId] = useState(null);
	const [isUploadModalVisible, setIsUploadModalVisible] = useState(false);
	const [selectedMedia, setSelectedMedia] = useState(null);
	const [isMediaPreviewVisible, setIsMediaPreviewVisible] = useState(false);
	const [deleteModalVisible, setDeleteModalVisible] = useState(false);
	const [mediaToDelete, setMediaToDelete] = useState(null);
	const [isFolderEditModalVisible, setIsFolderEditModalVisible] =
		useState(false);
	const [selectedFolder, setSelectedFolder] = useState(null);
	const [isDragging, setIsDragging] = useState(false);
	const [folderSectionWidth, setFolderSectionWidth] = useState(50);
	const dividerRef = useRef(null);
	const contentRef = useRef(null);
	const [searchQuery, setSearchQuery] = useState('');
	const [filteredFolders, setFilteredFolders] = useState([]);
	const [filteredFiles, setFilteredFiles] = useState([]);
	const { organization } = useAuth();
	const [deleteFolderModalVisible, setDeleteFolderModalVisible] =
		useState(false);
	const [folderToDelete, setFolderToDelete] = useState(null);
	const [deleteContainedMedia, setDeleteContainedMedia] = useState(false);
	const [isDraggingFiles, setIsDraggingFiles] = useState(false);
	const [draggedFiles, setDraggedFiles] = useState(null);
	const [draggedFolder, setDraggedFolder] = useState(null);
	const [folderContents, setFolderContents] = useState(null);
	const [folderSize, setFolderSize] = useState(0);
	const [acknowledgeLargeFolder, setAcknowledgeLargeFolder] = useState(false);
	const [selectedFiles, setSelectedFiles] = useState([]);
	const [isMultiSelectActive, setIsMultiSelectActive] = useState(false);
	const [isMoveModalVisible, setIsMoveModalVisible] = useState(false);
	const [isMultiDeleteModalVisible, setIsMultiDeleteModalVisible] =
		useState(false);
	const [isResizing, setIsResizing] = useState(false);
	const [folderDragCounter, setFolderDragCounter] = useState(0);
	const [mediaDragCounter, setMediaDragCounter] = useState(0);

	useEffect(() => {
		const loadData = async () => {
			if (!orgData?.id) {
				setLoading(false);
				return;
			}

			try {
				const [foldersData, tagsData, filesData] = await Promise.all([
					mediaApi.folders.getAll(orgData.id),
					tagsApi.getAll(orgData.id),
					mediaApi.files.getAll(orgData.id, currentFolderId),
				]);
				setFolders(foldersData.folders);
				setTags(tagsData.tags);
				setFiles(filesData.media);
			} catch (error) {
				console.error('Error loading data:', error);
				message.error('Failed to load data');
			} finally {
				setLoading(false);
			}
		};

		loadData();
	}, [orgData, currentFolderId]);

	useEffect(() => {
		const handleMouseMove = (e) => {
			if (!isDragging || !contentRef.current) return;

			const contentRect = contentRef.current.getBoundingClientRect();
			const contentWidth = contentRect.width;
			const mouseX = e.clientX - contentRect.left;

			const minFolderWidth = (300 / contentWidth) * 100;
			const minMediaWidth = (400 / contentWidth) * 100;

			let newWidth = (mouseX / contentWidth) * 100;
			newWidth = Math.max(minFolderWidth, Math.min(newWidth, 70));

			if (100 - newWidth < minMediaWidth) {
				newWidth = 100 - minMediaWidth;
			}

			setFolderSectionWidth(newWidth);
		};

		const handleMouseUp = () => {
			if (isDragging) {
				setIsDragging(false);
				setIsResizing(false);
			}
		};

		if (isDragging) {
			document.addEventListener('mousemove', handleMouseMove);
			document.addEventListener('mouseup', handleMouseUp);

			return () => {
				document.removeEventListener('mousemove', handleMouseMove);
				document.removeEventListener('mouseup', handleMouseUp);
			};
		}
	}, [isDragging, isResizing]);

	const handleDeleteFile = async (fileId) => {
		try {
			await mediaApi.files.delete(fileId);
			setFiles(files.filter((file) => file.id !== fileId));
			message.success('File deleted successfully');
		} catch (error) {
			console.error('Delete file error:', error);
			message.error('Failed to delete file');
		}
	};

	// Add a function to refresh the data
	const refreshData = async () => {
		setLoading(true);
		try {
			const [foldersData, tagsData, filesData] = await Promise.all([
				mediaApi.folders.getAll(orgData.id),
				tagsApi.getAll(orgData.id),
				mediaApi.files.getAll(orgData.id, currentFolderId),
			]);
			setFolders(foldersData.folders);
			setTags(tagsData.tags);
			setFiles(filesData.media);
		} catch (error) {
			console.error('Error refreshing data:', error);
			message.error('Failed to refresh data');
		} finally {
			setLoading(false);
		}
	};

	// Add this helper function to process folder contents
	const processFolderContents = async (items) => {
		const contents = {
			files: [],
			totalSize: 0,
			path: '',
		};

		const processEntry = async (entry, path = '') => {
			if (entry.isFile) {
				return new Promise((resolve) => {
					entry.file((file) => {
						contents.files.push({
							file,
							path: path ? `${path}/${file.name}` : file.name,
						});
						contents.totalSize += file.size;
						resolve();
					});
				});
			} else if (entry.isDirectory) {
				const dirReader = entry.createReader();
				contents.path = path;

				return new Promise((resolve) => {
					const readEntries = () => {
						dirReader.readEntries(async (entries) => {
							if (entries.length === 0) {
								resolve();
							} else {
								await Promise.all(
									entries.map((entry) =>
										processEntry(
											entry,
											path
												? `${path}/${entry.name}`
												: entry.name
										)
									)
								);
								readEntries();
							}
						});
					};
					readEntries();
				});
			}
		};

		for (let item of items) {
			const entry = item.webkitGetAsEntry();
			if (entry) {
				await processEntry(entry);
			}
		}

		return contents;
	};

	// Add these helper functions at the top level of the component
	const isChildOf = (child, parent) => {
		let node = child.parentNode;
		while (node != null) {
			if (node === parent) {
				return true;
			}
			node = node.parentNode;
		}
		return false;
	};

	// Update folder drag handlers
	const handleFolderDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();

		setFolderDragCounter((prev) => prev + 1);
		if (!isDragging) {
			setIsDragging(true);
		}
	};

	const handleFolderDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();

		setFolderDragCounter((prev) => prev - 1);
		// Only remove dragging state when counter reaches 0
		if (folderDragCounter === 1) {
			setIsDragging(false);
		}
	};

	const handleFolderDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleFolderDrop = async (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDragging(false);
		setFolderDragCounter(0); // Reset counter on drop

		const items = e.dataTransfer.items;
		if (items.length === 0) return;

		// Check if it's a folder
		const item = items[0];
		const entry = item.webkitGetAsEntry();

		if (entry && entry.isDirectory) {
			const contents = await processFolderContents([item]);
			setDraggedFolder(entry);
			setFolderContents(contents);
			setFolderSize(contents.totalSize);

			// If folder is larger than 1GB, require acknowledgment
			if (contents.totalSize > 1024 * 1024 * 1024) {
				setAcknowledgeLargeFolder(false);
			} else {
				setAcknowledgeLargeFolder(true);
			}

			setIsFolderCreateModalVisible(true);
		}
	};

	// Update the folder creation handler
	const handleFolderCreate = async (folderData) => {
		try {
			// Create the folder first
			const newFolder = await mediaApi.folders.create({
				...folderData,
				orgId: orgData.id,
			});

			if (folderContents && folderContents.files.length > 0) {
				// Upload files to S3 first
				const uploadPromises = folderContents.files.map(
					async (fileData) => {
						// Format the file name first
						const formattedFileName = formatFileName(
							fileData.file.name
						);

						// Construct the full path including folder structure
						const relativePath = fileData.path
							? formatFileName(
									fileData.path
										.split('/')
										.slice(0, -1)
										.join('/')
							  )
							: '';

						// Combine the paths, ensuring no double slashes
						const fullPath = `${newFolder.folder.id}/${formattedFileName}`;

						// Upload file to S3 with the full path
						const fileUrl = await s3Api.upload(
							orgData.id,
							fileData.file,
							fullPath,
							newFolder.folder.id
						);

						return {
							name: formattedFileName,
							fileUrl,
							fileType:
								fileData.file.type ||
								getFileType(fileData.file),
							folderId: newFolder.folder.id,
							organizationId: orgData.id,
							path: relativePath,
							thumbnailUrl: fileData.thumbnailUrl || undefined, // Add thumbnailUrl if it exists
						};
					}
				);

				const mediaItems = await Promise.all(uploadPromises);

				// Batch create media records
				await mediaApi.files.batchCreate(mediaItems);
			}

			await refreshData();
			message.success('Folder and contents uploaded successfully');
			setIsFolderCreateModalVisible(false);

			// Reset states
			setDraggedFolder(null);
			setFolderContents(null);
			setFolderSize(0);
			setAcknowledgeLargeFolder(false);
		} catch (error) {
			console.error('Folder creation error:', error);
			message.error(error.message || 'Failed to create folder');
		}
	};

	// Update this helper function
	const formatFileName = (fileName) => {
		// Check if the fileName already contains a path
		const baseName = fileName.split('/').pop(); // Get just the filename from any path

		// Remove file extension
		const lastDot = baseName.lastIndexOf('.');
		const nameWithoutExtension =
			lastDot !== -1 ? baseName.slice(0, lastDot) : baseName;
		const extension = lastDot !== -1 ? baseName.slice(lastDot) : '';

		// Format the name: lowercase, replace spaces with hyphens, remove special characters
		const formattedName = nameWithoutExtension
			.toLowerCase()
			.replace(/\s+/g, '-') // Replace spaces with hyphens
			.replace(/[^a-z0-9-]/g, '') // Remove special characters except hyphens
			.replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
			.replace(/^-+|-+$/g, ''); // Remove hyphens from start and end

		// Return formatted name with extension
		return formattedName + extension;
	};

	// Update the handleUploadSubmit function
	const handleUploadSubmit = async (values) => {
		try {
			// Extract file and formData from values
			const { file, formData, onProgress } = values;

			// Get form data values
			const name = formData.get('name');
			const folderId = formData.get('folderId');
			const tagId = formData.get('tagId');
			const filePath = formData.get('filePath');
			const thumbnailUrl = formData.get('thumbnailUrl');

			// Upload file to S3
			const fileUrl = await s3Api.upload(
				orgData.id,
				file,
				filePath,
				folderId,
				onProgress
			);

			// Create the media data object
			const mediaData = new FormData();
			mediaData.append('name', name);
			mediaData.append('fileUrl', fileUrl);
			mediaData.append('fileType', file.type || getFileType(file));
			mediaData.append('folderId', folderId);
			mediaData.append('organizationId', orgData.id);

			// Explicitly append thumbnailUrl if it exists
			if (thumbnailUrl) {
				mediaData.append('thumbnailUrl', thumbnailUrl);
			}

			// Only append tagId if it's a valid number
			if (tagId && !isNaN(parseInt(tagId))) {
				mediaData.append('tagId', parseInt(tagId));
			}

			// Create the media record in your database
			await mediaApi.files.upload(mediaData);

			await refreshData();
			return fileUrl;
		} catch (error) {
			console.error('Upload error:', error);
			throw error;
		}
	};

	// Helper function to get file type if not provided
	const getFileType = (file) => {
		if (file.type) return file.type;

		// Infer from extension
		const extension = file.name.split('.').pop().toLowerCase();
		const mimeTypes = {
			jpg: 'image/jpeg',
			jpeg: 'image/jpeg',
			png: 'image/png',
			gif: 'image/gif',
			pdf: 'application/pdf',
			mp4: 'video/mp4',
			mp3: 'audio/mpeg',
			// Add more as needed
		};

		return mimeTypes[extension] || `application/${extension}`;
	};

	const handleMediaUpdate = async (mediaId, updateData) => {
		try {
			await mediaApi.files.update(mediaId, updateData);
			await refreshData();
			message.success('Media updated successfully');
		} catch (error) {
			console.error('Update error:', error);
			message.error('Failed to update media');
		}
	};

	const handleDeleteClick = async (e, file) => {
		e.stopPropagation(); // Prevent row click event
		setMediaToDelete(file);
		setDeleteModalVisible(true);
	};

	const handleDeleteConfirm = async () => {
		try {
			// Delete from database first
			await mediaApi.files.delete(mediaToDelete.id);

			// If database deletion successful, delete from S3
			if (mediaToDelete.fileUrl) {
				const key = mediaToDelete.fileUrl.split('amazonaws.com/')[1];
				await s3Api.deleteFile(orgData.id, key);
			}

			setFiles(files.filter((f) => f.id !== mediaToDelete.id));
			message.success('File deleted successfully');
			setDeleteModalVisible(false);
			setMediaToDelete(null);
		} catch (error) {
			console.error('Delete error:', error);
			message.error('Failed to delete file');
		}
	};

	const handleFolderEdit = async (folderId, updateData) => {
		try {
			await mediaApi.folders.update(folderId, updateData);
			await refreshData();
			message.success('Folder updated successfully');
		} catch (error) {
			console.error('Folder update error:', error);
			message.error('Failed to update folder');
		}
	};

	const handleFolderClick = (folderOrAction) => {
		if (folderOrAction === 'back') {
			// Get parent folder ID from current folder
			const currentFolder = folders.find((f) => f.id === currentFolderId);
			setCurrentFolderId(currentFolder?.parentFolder || null);
		} else {
			// Regular folder click
			setCurrentFolderId(folderOrAction.id);
		}

		// Clear search when navigating to a folder
		setSearchQuery('');
		setFilteredFolders(folders);
		setFilteredFiles(files);
	};

	const handleEditClick = (e, folder) => {
		e.stopPropagation(); // Prevent folder click event
		setSelectedFolder(folder);
		setIsFolderEditModalVisible(true);
	};

	const handleDeleteFolder = async (e, folder) => {
		e.stopPropagation(); // Prevent folder click event

		// Check if folder has contents
		const hasSubfolders = folders.some((f) => f.parentFolder === folder.id);
		const hasFiles = files.some((f) => f.folderId === folder.id);

		if (hasSubfolders) {
			message.error('Cannot delete folder that contains subfolders');
			return;
		}

		setFolderToDelete(folder);
		setDeleteContainedMedia(false); // Reset checkbox
		setDeleteFolderModalVisible(true);
	};

	const handleDeleteFolderConfirm = async () => {
		try {
			const folderFiles = files.filter(
				(file) => file.folderId === folderToDelete.id
			);

			// Delete cover photo from S3 if it exists
			if (folderToDelete.coverPhotoUrl) {
				try {
					await s3Api.deleteFile(
						orgData.id,
						folderToDelete.coverPhotoUrl.split('amazonaws.com/')[1]
					);
				} catch (error) {
					console.error('Error deleting cover photo:', error);
					// Continue with folder deletion even if cover photo deletion fails
				}
			}

			if (folderFiles.length > 0) {
				// Handle S3 operations first
				const processedFiles = await s3Api.handleFolderContents(
					orgData.id,
					folderFiles,
					deleteContainedMedia
				);

				// Update database based on S3 operations
				for (const file of processedFiles) {
					if (deleteContainedMedia) {
						await mediaApi.files.delete(file.id);
					} else if (file.newUrl) {
						await mediaApi.files.update(file.id, {
							fileUrl: file.newUrl,
							folderId: null,
							name: file.name,
							fileType: file.fileType,
							organizationId: orgData.id,
						});
					}
				}
			}

			// Delete the folder from database
			await mediaApi.folders.delete(folderToDelete.id);

			message.success(
				deleteContainedMedia
					? 'Folder and contents deleted successfully'
					: 'Files moved to root folder and folder deleted successfully'
			);

			refreshData();
			setDeleteFolderModalVisible(false);
			setFolderToDelete(null);
			setDeleteContainedMedia(false);
		} catch (error) {
			console.error('Delete error:', error);
			message.error('Failed to delete folder: ' + error.message);
		}
	};

	const LoadingSkeleton = () => (
		<div className='dashboard-page'>
			<div className='page-header'>
				<Skeleton.Input
					style={{ width: 200 }}
					active
				/>
				<Skeleton.Button active />
			</div>

			<div className='content-section'>
				<div className='admin-controls'>
					<Skeleton.Input
						style={{ width: '100%' }}
						active
					/>
				</div>

				<Layout style={{ background: 'transparent' }}>
					<Content className='dashboard-content'>
						<div className='detail-card'>
							<Skeleton.Input
								style={{ width: 150, marginBottom: 16 }}
								active
							/>
							<div className='folders-grid'>
								{[...Array(4)].map((_, index) => (
									<Skeleton.Node
										key={index}
										style={{ width: '100%', height: 120 }}
										active
									/>
								))}
							</div>
						</div>

						<div className='detail-card'>
							<Skeleton.Input
								style={{ width: 150, marginBottom: 16 }}
								active
							/>
							<div className='media-grid'>
								{[...Array(6)].map((_, index) => (
									<Skeleton.Node
										key={index}
										style={{ width: '100%', height: 120 }}
										active
									/>
								))}
							</div>
						</div>
					</Content>
				</Layout>
			</div>
		</div>
	);

	// Add breadcrumb navigation
	const getCurrentFolderName = () => {
		if (!currentFolderId) return 'Root';
		const folder = folders.find((f) => f.id === currentFolderId);
		return folder ? folder.name : 'Root';
	};

	// Add this helper function before the DashboardFiles component
	const getFileTypeIcon = (fileType) => {
		if (!fileType) return <FileOutlined />;

		if (
			fileType.startsWith('image/') ||
			fileType.includes('jpg') ||
			fileType.includes('jpeg')
		)
			return <FileImageOutlined />;
		if (fileType.startsWith('video/')) return <VideoCameraOutlined />;
		if (fileType.startsWith('audio/')) return <AudioOutlined />;

		switch (fileType.toLowerCase()) {
			case 'application/pdf':
				return <FilePdfOutlined />;
			case 'application/msword':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
				return <FileWordOutlined />;
			case 'application/vnd.ms-excel':
			case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
				return <FileExcelOutlined />;
			case 'application/vnd.ms-powerpoint':
			case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
				return <FilePptOutlined />;
			default:
				return <FileOutlined />;
		}
	};

	// Add this helper function near getFileTypeIcon
	const getReadableFileType = (mimeType) => {
		if (!mimeType) return 'Unknown Type';

		// Handle file extensions directly
		if (mimeType === 'jpg' || mimeType === 'jpeg') {
			return 'Image (JPEG)';
		}
		if (mimeType === 'png') {
			return 'Image (PNG)';
		}
		if (mimeType === 'gif') {
			return 'Image (GIF)';
		}

		// Handle MIME types
		if (mimeType.startsWith('image/')) {
			const subtype = mimeType.split('/')[1];
			return `Image (${subtype.toUpperCase()})`;
		}
		if (mimeType.startsWith('video/')) {
			const subtype = mimeType.split('/')[1];
			return `Video (${subtype.toUpperCase()})`;
		}
		if (mimeType.startsWith('audio/')) {
			const subtype = mimeType.split('/')[1];
			return `Audio (${subtype.toUpperCase()})`;
		}

		// Handle specific application types
		switch (mimeType.toLowerCase()) {
			case 'application/pdf':
				return 'PDF Document';
			case 'application/msword':
			case 'application/vnd.openxmlformats-officedocument.wordprocessingml.document':
				return 'Word Document';
			case 'application/vnd.ms-excel':
			case 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet':
				return 'Excel Spreadsheet';
			case 'application/vnd.ms-powerpoint':
			case 'application/vnd.openxmlformats-officedocument.presentationml.presentation':
				return 'PowerPoint Presentation';
			default:
				// Try to extract subtype from MIME type if possible
				if (mimeType.includes('/')) {
					const subtype = mimeType.split('/')[1];
					return `File (${subtype.toUpperCase()})`;
				}
				return `File (${mimeType.toUpperCase()})`;
		}
	};

	// Simplified search handler that only triggers on submit
	const handleSearch = async (value) => {
		setSearchQuery(value);

		if (!value.trim()) {
			setFilteredFolders(folders);
			setFilteredFiles(files);
			return;
		}

		try {
			const searchResults = await mediaApi.files.search(
				orgData.id,
				value
			);

			setFilteredFolders(searchResults.folderResults || []);
			setFilteredFiles(searchResults.fileResults || []);
		} catch (error) {
			console.error('Search error:', error);
			message.error('Failed to search: ' + error.message);
			setFilteredFolders(folders);
			setFilteredFiles(files);
		}
	};

	// Simple input handler that only updates the input value
	const handleSearchInput = (e) => {
		setSearchQuery(e.target.value);
	};

	// Initialize filtered results
	useEffect(() => {
		setFilteredFolders(folders);
		setFilteredFiles(files);
	}, [folders, files]);

	// Update media section drag handlers
	const handleDragEnter = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingFiles(true);
	};

	const handleDragLeave = (e) => {
		e.preventDefault();
		e.stopPropagation();
		if (!e.currentTarget.contains(e.relatedTarget)) {
			setIsDraggingFiles(false);
		}
	};

	const handleDragOver = (e) => {
		e.preventDefault();
		e.stopPropagation();
	};

	const handleDrop = (e) => {
		e.preventDefault();
		e.stopPropagation();
		setIsDraggingFiles(false);

		const files = Array.from(e.dataTransfer.files);
		if (files.length > 0) {
			setDraggedFiles(files);
			setIsUploadModalVisible(true);
		}
	};

	// Add these handlers
	const handleFileSelect = (file, checked) => {
		if (checked) {
			setSelectedFiles([...selectedFiles, file]);
		} else {
			setSelectedFiles(selectedFiles.filter((f) => f.id !== file.id));
		}
		setIsMultiSelectActive(true);
	};

	// Add this function near the other selection-related functions
	const clearSelection = () => {
		setSelectedFiles([]);
		setIsMultiSelectActive(false);
	};

	const handleMoveFiles = async (targetFolderId) => {
		try {
			const movePromises = selectedFiles.map(async (file) => {
				const newFileUrl = await s3Api.moveFile(
					orgData.id,
					file.fileUrl,
					targetFolderId,
					formatFileName(file.name)
				);
				return { id: file.id, fileUrl: newFileUrl };
			});

			const movedFiles = await Promise.all(movePromises);

			// Update database records
			const updatePromises = movedFiles.map(({ id, fileUrl }) =>
				mediaApi.files.update(id, {
					folderId: targetFolderId === 'root' ? null : targetFolderId,
					fileUrl,
				})
			);

			await Promise.all(updatePromises);

			message.success('Files moved successfully');
			setIsMoveModalVisible(false);
			clearSelection();
			refreshData();
		} catch (error) {
			console.error('Move files error:', error);
			message.error('Failed to move files: ' + error.message);
		}
	};

	const handleMultiDelete = async () => {
		try {
			// First delete from S3
			await Promise.all(
				selectedFiles.map((file) =>
					file.fileUrl
						? s3Api.deleteFile(
								orgData.id,
								file.fileUrl.split('amazonaws.com/')[1]
						  )
						: Promise.resolve()
				)
			);

			// Then batch delete from database
			await mediaApi.files.batchDelete(
				selectedFiles.map((file) => file.id)
			);

			message.success(
				`Successfully deleted ${selectedFiles.length} files`
			);
			setIsMultiDeleteModalVisible(false);
			setSelectedFiles([]);
			setIsMultiSelectActive(false);
			await refreshData();
		} catch (error) {
			console.error('Delete files error:', error);
			message.error(
				'Failed to delete files: ' + (error.message || 'Unknown error')
			);
		}
	};

	// Update the media section render to include pointer-events: none on child elements
	const renderMediaSection = () => (
		<div
			className={`detail-card media-section ${
				isDraggingFiles ? 'dragging' : ''
			}`}
			onDragEnter={handleDragEnter}
			onDragLeave={handleDragLeave}
			onDragOver={handleDragOver}
			onDrop={handleDrop}>
			{isDraggingFiles && (
				<div className='drag-overlay'>
					<div className='drag-message'>
						<UploadOutlined />
						<span>Drop files here to upload</span>
					</div>
				</div>
			)}
			<div
				className='section-header'
				style={{ pointerEvents: isDraggingFiles ? 'none' : 'auto' }}>
				<h2>Media</h2>
				<div className='section-actions'>
					{isMultiSelectActive && selectedFiles.length > 0 && (
						<div
							style={{
								display: 'flex',
								gap: 8,
								flexWrap: 'wrap',
								justifyContent: 'center	',
							}}>
							<Button
								icon={<FolderOutlined />}
								onClick={() => setIsMoveModalVisible(true)}>
								Move ({selectedFiles.length})
							</Button>
							<Button
								icon={<DeleteOutlined />}
								danger
								onClick={() =>
									setIsMultiDeleteModalVisible(true)
								}>
								Delete ({selectedFiles.length})
							</Button>
						</div>
					)}
					<Button
						type='primary'
						icon={<PlusOutlined />}
						onClick={() => setIsUploadModalVisible(true)}
						className='section-button'>
						Upload
					</Button>
				</div>
			</div>
			<div
				className='table-responsive'
				style={{ pointerEvents: isDraggingFiles ? 'none' : 'auto' }}>
				{Array.isArray(searchQuery ? filteredFiles : files) &&
				(searchQuery ? filteredFiles : files).length > 0 ? (
					<table className='users-table'>
						<thead>
							<tr>
								<th style={{ width: '40px' }}>
									<Checkbox
										checked={
											selectedFiles.length ===
											(searchQuery
												? filteredFiles
												: files
											).filter(
												(file) =>
													file.folderId ===
													currentFolderId
											).length
										}
										onChange={(e) => {
											const currentFiles = (
												searchQuery
													? filteredFiles
													: files
											).filter(
												(file) =>
													file.folderId ===
													currentFolderId
											);
											if (e.target.checked) {
												setSelectedFiles(currentFiles);
												setIsMultiSelectActive(true);
											} else {
												setSelectedFiles([]);
												setIsMultiSelectActive(false);
											}
										}}
									/>
								</th>
								<th style={{ textAlign: 'left', width: '50%' }}>
									Name
								</th>
								<th
									style={{
										textAlign: 'center',
										width: '20%',
									}}>
									Type
								</th>
								<th
									style={{
										textAlign: 'right',
										width: '20%',
									}}>
									Actions
								</th>
							</tr>
						</thead>
						<tbody>
							{(searchQuery ? filteredFiles : files)
								.filter((file) =>
									!searchQuery
										? file.folderId === currentFolderId
										: true
								)
								.map((file) => (
									<tr key={file.id}>
										<td>
											<Checkbox
												checked={selectedFiles.some(
													(f) => f.id === file.id
												)}
												onChange={(e) =>
													handleFileSelect(
														file,
														e.target.checked
													)
												}
												onClick={(e) =>
													e.stopPropagation()
												}
											/>
										</td>
										<td
											style={{ textAlign: 'left' }}
											onClick={() => {
												setSelectedMedia(file);
												setIsMediaPreviewVisible(true);
											}}>
											{file.name}
										</td>
										<td style={{ textAlign: 'center' }}>
											<span
												title={getReadableFileType(
													file.fileType
												)}>
												{getFileTypeIcon(file.fileType)}
											</span>
										</td>
										<td style={{ textAlign: 'right' }}>
											<button
												className='action-btn delete-btn'
												onClick={(e) =>
													handleDeleteClick(e, file)
												}
												title='Delete media'>
												<DeleteOutlined />
											</button>
										</td>
									</tr>
								))}
						</tbody>
					</table>
				) : (
					<Empty description='No files found' />
				)}
			</div>

			{/* Add Move Modal */}
			<Modal
				title='Move Files'
				open={isMoveModalVisible}
				onCancel={() => setIsMoveModalVisible(false)}
				footer={null}>
				<div style={{ maxHeight: '400px', overflow: 'auto' }}>
					{folders.map((folder) => (
						<div
							key={folder.id}
							className='folder-option'
							onClick={() => handleMoveFiles(folder.id)}>
							<FolderOutlined /> {folder.name}
						</div>
					))}
				</div>
			</Modal>

			{/* Add Delete Confirmation Modal */}
			<Modal
				title='Delete Files'
				open={isMultiDeleteModalVisible}
				onOk={handleMultiDelete}
				onCancel={() => setIsMultiDeleteModalVisible(false)}
				okText='Delete'
				okButtonProps={{ danger: true }}>
				<p>
					Are you sure you want to delete {selectedFiles.length}{' '}
					files?
				</p>
				<p>This action cannot be undone.</p>
			</Modal>
		</div>
	);

	// Update the folder section render to include pointer-events: none on child elements
	const renderFolderSection = () => (
		<div
			className={`detail-card folder-section ${
				isDragging && !isResizing ? 'dragging' : ''
			}`}
			onDragEnter={handleFolderDragEnter}
			onDragLeave={handleFolderDragLeave}
			onDragOver={handleFolderDragOver}
			onDrop={handleFolderDrop}>
			<div
				className='section-header'
				style={{ pointerEvents: isDragging ? 'none' : 'auto' }}>
				<h2>Folders</h2>
				<Button
					type='primary'
					icon={<PlusOutlined />}
					onClick={() => setIsFolderCreateModalVisible(true)}
					className='section-button'>
					Create Folder
				</Button>
			</div>
			{isDragging && !isResizing && (
				<div className='drag-overlay'>
					<div className='drag-message'>
						<FolderAddOutlined />
						<span>Drop folder here to upload</span>
					</div>
				</div>
			)}
			<div
				className='folders-grid'
				style={{ pointerEvents: isDragging ? 'none' : 'auto' }}>
				{!searchQuery && currentFolderId && (
					<div
						className='folder-card'
						onClick={() => handleFolderClick('back')}>
						<div className='folder-icon'>
							<ArrowUpOutlined />
						</div>
						<div className='folder-name'>Back</div>
					</div>
				)}
				{(searchQuery ? filteredFolders : folders)
					.filter((folder) =>
						!searchQuery
							? folder.parentFolder === currentFolderId
							: true
					)
					.map((folder) => (
						<div
							key={folder.id}
							className='folder-card'
							onClick={() => handleFolderClick(folder)}>
							<div className='folder-icon'>
								{folder.coverPhotoUrl ? (
									<img
										src={folder.coverPhotoUrl}
										alt={folder.name}
										className='folder-cover-image'
									/>
								) : (
									<FolderOutlined />
								)}
							</div>
							<div className='folder-name'>{folder.name}</div>
							<div className='folder-actions'>
								<button
									className='action-btn edit-btn'
									onClick={(e) => handleEditClick(e, folder)}
									title='Edit folder'>
									<EditOutlined />
								</button>
								<button
									className='action-btn delete-btn'
									onClick={(e) =>
										handleDeleteFolder(e, folder)
									}
									title='Delete folder'>
									<DeleteOutlined />
								</button>
							</div>
						</div>
					))}
			</div>
		</div>
	);

	const handleMouseDown = (e) => {
		e.preventDefault();
		setIsResizing(true);
		setIsDragging(true);
	};

	// Add cleanup effect
	useEffect(() => {
		return () => {
			// Reset all drag states when component unmounts
			setIsDragging(false);
			setIsDraggingFiles(false);
			setFolderDragCounter(0);
			setMediaDragCounter(0);
		};
	}, []);

	if (loading) {
		return (
			<DashboardLayout>
				<LoadingSkeleton />
			</DashboardLayout>
		);
	}

	return (
		<DashboardLayout>
			<div className='dashboard-page'>
				{/* Page Header */}
				<div className='page-header'>
					<h1>Files</h1>
				</div>

				{/* Content Section */}
				<div className='content-section'>
					{/* Search and Filters */}
					<div className='admin-controls'>
						<div className='admin-search-container'>
							<Search
								placeholder='Search folders and files...'
								className='search-input'
								value={searchQuery}
								onChange={handleSearchInput}
								onSearch={handleSearch}
								allowClear
								enterButton={<SearchOutlined />}
							/>
						</div>
						<Button
							type={showFilters ? 'primary' : 'default'}
							icon={
								showFilters ? (
									<CloseOutlined />
								) : (
									<FilterOutlined />
								)
							}
							onClick={() => setShowFilters(!showFilters)}>
							Filters
						</Button>
					</div>

					{/* Updated Folder Navigation - moved above Layout */}
					<div className='folder-navigation'>
						<span
							className={`folder-path ${
								!currentFolderId ? 'active' : ''
							}`}
							onClick={() => setCurrentFolderId(null)}>
							<HomeOutlined />
							Root
						</span>

						{currentFolderId && (
							<>
								<RightOutlined className='folder-separator' />
								<span className='folder-path active'>
									<FolderOutlined />
									{getCurrentFolderName()}
								</span>
							</>
						)}
					</div>

					<Layout style={{ background: 'transparent' }}>
						<Content
							className='dashboard-content'
							ref={contentRef}
							style={{
								display: 'flex',
								padding: '24px',
								position: 'relative',
								minHeight: '500px',
								gap: '0',
							}}>
							{/* Left Section */}
							<div
								className='folder-section-container'
								style={{
									width: `calc(${folderSectionWidth}% - 6px)`, // Subtract half the gap
									transition: isDragging
										? 'none'
										: 'width 0.1s ease',
								}}>
								{renderFolderSection()}
							</div>

							{/* Center Divider Section */}
							<div className='divider-container'>
								<div
									className={`resizable-divider ${
										isDragging ? 'dragging' : ''
									}`}
									onMouseDown={handleMouseDown}
								/>
							</div>

							{/* Right Section */}
							<div
								className='media-section-container'
								style={{
									width: `calc(${
										100 - folderSectionWidth
									}% - 6px)`, // Subtract half the gap
									transition: isDragging
										? 'none'
										: 'width 0.1s ease',
								}}>
								{renderMediaSection()}
							</div>
						</Content>

						{/* Filters Sidebar */}
						<Sider
							className='filters-sider'
							theme='light'
							width={300}
							collapsed={!showFilters}
							collapsedWidth={0}
							style={{
								background: 'var(--surface-color, #fff)',
								borderLeft:
									'1px solid var(--border-color, #f0f0f0)',
							}}>
							<div className='filters-content'>
								<h3>Filters</h3>
								{/* Filter components will go here */}
							</div>
						</Sider>
					</Layout>
				</div>

				{/* Upload Modal */}
				<FolderCreateModal
					visible={isFolderCreateModalVisible}
					onClose={() => {
						setIsFolderCreateModalVisible(false);
						setDraggedFolder(null);
						setFolderContents(null);
						setFolderSize(0);
						setAcknowledgeLargeFolder(false);
					}}
					onSubmit={handleFolderCreate}
					folders={folders}
					tags={tags}
					currentFolderId={currentFolderId}
					loading={loading}
					folderSize={folderSize}
					requireAcknowledgment={folderSize > 1024 * 1024 * 1024}
					onAcknowledgmentChange={setAcknowledgeLargeFolder}
					acknowledged={acknowledgeLargeFolder}
				/>
				<FileUploadModal
					visible={isUploadModalVisible}
					onClose={() => {
						setIsUploadModalVisible(false);
						setDraggedFiles(null);
					}}
					onSubmit={handleUploadSubmit}
					folders={folders}
					tags={tags}
					currentFolderId={currentFolderId}
					organizationId={orgData?.id}
					loading={loading}
					preSelectedFiles={draggedFiles}
					hideLocationSelect={draggedFiles && draggedFiles.length > 1}
					defaultFolderId={currentFolderId}
				/>

				{/* Add MediaPreviewModal */}
				<MediaPreviewModal
					visible={isMediaPreviewVisible}
					onClose={() => {
						setIsMediaPreviewVisible(false);
						setSelectedMedia(null);
					}}
					media={selectedMedia}
					folders={folders}
					tags={tags}
					onUpdate={handleMediaUpdate}
					organizationId={orgData?.id}
				/>

				{/* Add Delete Confirmation Modal */}
				<Modal
					title='Delete Media'
					open={deleteModalVisible}
					onOk={handleDeleteConfirm}
					onCancel={() => {
						setDeleteModalVisible(false);
						setMediaToDelete(null);
					}}
					okText='Delete'
					okButtonProps={{ danger: true }}
					cancelText='Cancel'>
					<p>
						Are you sure you want to delete "{mediaToDelete?.name}"?
						This action cannot be undone.
					</p>
				</Modal>

				{/* Add FolderEditModal */}
				<FolderEditModal
					visible={isFolderEditModalVisible}
					onClose={() => {
						setIsFolderEditModalVisible(false);
						setSelectedFolder(null);
					}}
					onSubmit={handleFolderEdit}
					folders={folders}
					tags={tags}
					currentFolder={selectedFolder}
					loading={loading}
				/>

				{/* Add Folder Delete Confirmation Modal */}
				<Modal
					title='Delete Folder'
					open={deleteFolderModalVisible}
					onCancel={() => {
						setDeleteFolderModalVisible(false);
						setFolderToDelete(null);
						setDeleteContainedMedia(false);
					}}
					onOk={handleDeleteFolderConfirm}
					okText='Delete'
					okButtonProps={{
						danger: true,
					}}
					cancelText='Cancel'
					className='common-modal'>
					<p>
						Are you sure you want to delete the folder "
						{folderToDelete?.name}"?
					</p>

					{files.some(
						(file) => file.folderId === folderToDelete?.id
					) && (
						<div style={{ marginTop: '16px' }}>
							<Checkbox
								checked={deleteContainedMedia}
								onChange={(e) =>
									setDeleteContainedMedia(e.target.checked)
								}>
								Also delete all media within this folder
							</Checkbox>
							{deleteContainedMedia ? (
								<div className='warning-text'>
									Warning: All media files in this folder will
									be permanently deleted from your account.
								</div>
							) : (
								<div className='info-text'>
									Note: All media files will be moved to the
									root folder.
								</div>
							)}
						</div>
					)}
				</Modal>
			</div>
		</DashboardLayout>
	);
}

export default DashboardFiles;
